import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { CountPill } from "@/components/memo/ui/CountPill";
import { formatClosureDate } from "@/lib/format";
import { t } from "@/lib/strings";
import { showToast } from "@/lib/toast";
import type { MemoEntry } from "@/lib/types";

interface Props {
  entry: MemoEntry;
  canEdit: boolean;
  editing: boolean;
  /** Presentation: this heading is from an EARLIER meeting — its text and
   * existence are protected. Retire + move stay allowed. */
  historyLocked?: boolean;
  /** Series editors/admins can unlock while presenting; a participant cannot, so
   *  the "why did nothing happen" toast must not point at a button they lack. */
  mayEditHistory?: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  /** Persist the heading text. */
  onCommit: (body: string) => void;
  /** Enter in the heading editor → start a fresh point beneath it. */
  onEnterNext: () => void;
  /** Toggle the "close" (retire) state — the parent gates a real close behind a
   * confirm and cascades to the member points. */
  onToggleRetire: () => void;
  onDelete: () => void;
  /** "In Abschnitt verschieben" control (moves the heading + its points). */
  moveMenu?: React.ReactNode;
}

/**
 * A persistent sub-heading (Zwischenüberschrift) inside an Abschnitt — e.g.
 * "COMPANY XYZ". It stands over the points beneath it (positional grouping) and
 * carries forward on its own until closed; it is never a to-do.
 */
export function MemoSubheadingRow({
  entry,
  canEdit,
  editing: editingProp,
  historyLocked = false,
  mayEditHistory = false,
  onStartEdit,
  onStopEdit,
  onCommit,
  onEnterNext,
  onToggleRetire,
  onDelete,
  moveMenu,
}: Props) {
  // A locked heading is never in edit mode, whoever set `editing` — the
  // empty-backspace merge can focus it (see MemoEntryRow for the full story).
  const editing = editingProp && !historyLocked;
  const [draft, setDraft] = useState(entry.body);
  // 🔴 The last text SENT in this edit session (not `entry.body`, which lags until the save
  // round-trips). Enter saves and opens the next point; the input then unmounts and its blur saved
  // the same text a second time, because the cached heading still held the old text: two PATCHes,
  // two series walks on an older Protokoll, two toasts when the save fails. Same dedupe as
  // MemoEntryRow's committedRef, re-baselined only when a session opens.
  const sentRef = useRef(entry.body);
  const wasEditing = useRef(editing);
  useEffect(() => {
    if (editing && !wasEditing.current) {
      setDraft(entry.body);
      sentRef.current = entry.body;
    }
    wasEditing.current = editing;
  }, [editing, entry.body]);

  const commit = () => {
    const body = draft.trim();
    if (body === sentRef.current) return;
    sentRef.current = body;
    onCommit(body);
  };

  const labelStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return (
    <div
      id={`memo-entry-${entry.id}`}
      className="flow-memo-entry"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 2,
        // 🔴 The section divider is NOT drawn here. The rule + top spacing that
        // announce a new Abschnitt live on the section container
        // (MemoSection.tsx — borderTop / paddingTop / marginTop). This row used
        // to set the identical trio, so every Zwischenüberschrift rendered TWO
        // parallel hairlines ~26px apart. Do not re-add them.
        //
        // The intent behind them is unchanged and still holds: the rule and the
        // accent bar below stay full-strength even when the heading is closed —
        // otherwise a retired heading dims away and folds into the section above
        // it (the "closed Zwischenüberschrift reads as a sub-entry" bug). Only
        // the TEXT greys out (below) to signal "done".
      }}
    >
      <span
        style={{ width: 6, height: 15, borderRadius: 2, background: "var(--ink-3)", flexShrink: 0 }}
      />
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => {
            commit();
            onStopEdit();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
              onEnterNext();
            } else if (e.key === "Escape") {
              onStopEdit();
            }
          }}
          placeholder={t.memoSubheadingPlaceholder}
          style={{
            ...labelStyle,
            color: "var(--ink-1)",
            background: "var(--surface-soft)",
            border: "1px solid var(--hairline)",
            borderRadius: 6,
            padding: "3px 8px",
          }}
        />
      ) : (
        <span
          onClick={() => {
            // Silent no-ops send people hunting: say WHY the click did nothing.
            if (canEdit && historyLocked) {
              showToast(mayEditHistory ? t.memoPresentHistoryLocked : t.memoHistoryLockedNoRight);
              return;
            }
            if (canEdit) onStartEdit();
          }}
          title={canEdit ? t.memoSubheadingPlaceholder : undefined}
          style={{
            ...labelStyle,
            color: entry.body.trim() ? "var(--ink-1)" : "var(--ink-4)",
            cursor: canEdit ? "text" : "default",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: entry.retired ? "line-through" : undefined,
            // The dimming lives HERE, not on the row, so the divider + accent
            // bar above stay full-strength on a closed heading.
            opacity: entry.retired ? 0.5 : 1,
          }}
        >
          {entry.body.trim() || t.memoSubheadingPlaceholder}
        </span>
      )}
      {/* 🔴 Deliberately NOT gated on `finalized` — unlike the point-level badge in
       * MemoEntryRow, which hides until the memo is minutes because a draft's retired
       * lines are still being worked. A SECTION is different: closing a
       * Zwischenüberschrift is a structural decision that reads as closed the moment
       * it is made, in a draft just as much as in finalized minutes. That is why this
       * row no longer takes a `finalized` prop at all. Do not "fix" the asymmetry.
       * `retired_at` is still required — a retired heading from before the column was
       * backfilled would otherwise render a naked, dateless "abgeschlossen". */}
      {entry.retired && entry.retired_at ? (
        <span style={{ flexShrink: 0 }}>
          <CountPill tone="neutral">
            <Icon name="check" size={12} />
            abgeschlossen {formatClosureDate(entry.retired_at)}
          </CountPill>
        </span>
      ) : null}
      {canEdit ? (
        <div className="flow-memo-actions" style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {moveMenu}
          <button
            type="button"
            title={t.memoSubheadingClose}
            onClick={onToggleRetire}
            style={iconBtn}
          >
            <Icon name={entry.retired ? "refresh" : "check"} size={14} color="var(--ink-4)" />
          </button>
          {historyLocked ? null : (
            <button type="button" title="Löschen" onClick={onDelete} style={iconBtn}>
              <Icon name="trash" size={13} color="var(--ink-4)" />
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 3,
  lineHeight: 0,
};
